<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { Tooltip } from "@/components/ui/tooltip";
import { renderMarkdownInline } from "@utils/renderMarkdown";
import BarChartTooltip from "./BarChartTooltip.vue";

interface BarItem {
  code?: string;
  label: string;
  value: number;
  count: number;
  color?: string;
}

interface BarPopulation {
  label: string;
  color: string;
  data: BarItem[];
}

const props = defineProps<{
  populations: BarPopulation[];
  showSignificance?: boolean;
}>();

const revealed = inject("resultsCardRevealed", ref(true));

const rows = computed(() =>
  (props.populations[0]?.data || []).map((item, idx) => ({
    key: item.code || String(idx),
    item,
    idx,
    points: props.populations.map((pop) => ({ value: (pop.data[idx] || item).value || 0, color: pop.color })),
  })),
);

const tooltipPopulations = computed(() =>
  props.populations.map((pop) => ({ ...pop, data: pop.data.map(({ color: _color, ...item }) => item) })),
);

const maxValue = computed(() => Math.max(0.1, ...rows.value.flatMap((row) => row.points.map((point) => point.value))));

const position = (value: number) => (revealed.value ? (value / maxValue.value) * 100 : 0);

function rowRange(points: { value: number }[]) {
  const values = points.map((point) => position(point.value));
  return { min: Math.min(...values), max: Math.max(...values) };
}

const formatPercent = (value: number) => `${Math.round(value * 100)}%`;
</script>

<template>
  <div class="flex flex-col gap-3">
    <div v-if="populations.length > 1" class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      <span v-for="pop in populations" :key="`legend-${pop.label}`" class="flex items-center gap-1.5">
        <span class="inline-block h-2.5 w-2.5 rounded-full" :style="{ backgroundColor: pop.color }" />
        {{ pop.label }}
      </span>
    </div>

    <div v-for="row in rows" :key="row.key" class="flex items-center gap-2">
      <div class="w-[160px] shrink-0">
        <Tooltip trigger-class="block min-w-0 !cursor-default">
          <span
            class="block text-xs leading-[15px] font-medium line-clamp-2 break-words text-right"
            v-html="renderMarkdownInline(row.item.label)" />
          <template #content>
            <span v-html="renderMarkdownInline(row.item.label)" />
          </template>
        </Tooltip>
      </div>
      <Tooltip :delay-duration="0" trigger-class="flex-1 min-w-0 !cursor-default">
        <div class="relative h-5">
          <div class="absolute inset-x-0 top-1/2 h-px bg-border" />
          <div
            class="absolute top-1/2 h-0.5 -translate-y-1/2 bg-border-strong transition-all duration-300"
            :style="{ left: `${rowRange(row.points).min}%`, width: `${rowRange(row.points).max - rowRange(row.points).min}%` }" />
          <span
            v-for="(point, pointIdx) in row.points"
            :key="`dot-${row.key}-${pointIdx}`"
            class="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background shadow-sm transition-all duration-300"
            :style="{ left: `${position(point.value)}%`, backgroundColor: point.color }" />
        </div>
        <template #content>
          <BarChartTooltip :item="row.item" :item-idx="row.idx" :populations="tooltipPopulations" :show-significance="showSignificance" />
        </template>
      </Tooltip>
      <span class="w-10 shrink-0 text-right text-xs font-medium tabular-nums">
        {{ formatPercent(Math.max(...row.points.map((point) => point.value))) }}
      </span>
    </div>
  </div>
</template>
